/**
 * Geometria das áreas do filtro dos "Últimos focos": baixa a camada de
 * polígonos pelo carregador de vetores (`ensureDataSource` — worker, cache da
 * sessão, novas tentativas), acha as feições da área pela chave e monta o
 * teste rápido de ponto-no-polígono (`areaShape.ts`). O resultado fica em
 * cache por área.
 */
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type MultiPolygon from 'ol/geom/MultiPolygon';
import type Polygon from 'ol/geom/Polygon';
import type VectorSource from 'ol/source/Vector';
import { ensureDataSource, retryFailed } from '../vectors/loader';
import { useVectorStore } from '../vectors/store';
import { buildAreaShape, type AreaShape } from './areaShape';
import type { AreaMatcher } from './areaFires';
import { areaId, areaKind, type AreaRef } from './areaSearch';

/** A camada da área não carregou (rede/servidor): vale tentar de novo. */
export class AreaLoadError extends Error {}

/** Área que não existe nos dados (ex.: link antigo). */
export class AreaMissingError extends Error {}

/** Teste rápido a partir de um polígono ou multipolígono do OpenLayers; null para outros tipos. */
export function shapeFromGeometry(geometry: Geometry | undefined): AreaShape | null {
  if (!geometry) return null;
  const type = geometry.getType();
  if (type === 'Polygon') {
    const g = geometry as Polygon;
    return buildAreaShape(g.getFlatCoordinates(), g.getEnds());
  }
  if (type === 'MultiPolygon') {
    const g = geometry as MultiPolygon;
    // Os fins de anel do MultiPolygon já são índices absolutos em `flatCoordinates`.
    return buildAreaShape(g.getFlatCoordinates(), g.getEndss().flat());
  }
  return null;
}

/** Monta o critério da área a partir das feições já carregadas da camada. */
export function matcherFromFeatures(ref: Pick<AreaRef, 'layer' | 'key'>, features: readonly Feature<Geometry>[]): AreaMatcher | null {
  const kind = areaKind(ref.layer);
  const shapes: AreaShape[] = [];
  let cdUf = 0;
  for (const f of features) {
    const v = f.get(kind.keyField) as unknown;
    if (v == null || String(v).trim() !== ref.key) continue;
    const shape = shapeFromGeometry(f.getGeometry());
    if (shape) shapes.push(shape);
    if (ref.layer === 'estados') cdUf = Number(f.get('cd_uf')) || 0;
  }
  if (!shapes.length) return null;
  let byCode: AreaMatcher['byCode'] = null;
  if (ref.layer === 'municipios') {
    const code = Number(ref.key);
    if (Number.isFinite(code) && code > 0) byCode = (cd) => cd === code;
  } else if (ref.layer === 'estados' && cdUf > 0) {
    // Os 2 primeiros dígitos do código IBGE do município são o código da UF.
    byCode = (cd) => Math.floor(cd / 100_000) === cdUf;
  }
  return { byCode, shapes };
}

/** Área pronta: critério dos focos e geometrias (para o destaque no mapa). */
export interface ResolvedArea {
  matcher: AreaMatcher;
  geometries: Geometry[];
}

const cache = new Map<string, Promise<ResolvedArea>>();

/** Área já resolvida (baixa a camada se preciso). Rejeita com AreaLoadError ou AreaMissingError. */
export function resolveArea(ref: Pick<AreaRef, 'layer' | 'key'>): Promise<ResolvedArea> {
  const id = areaId(ref);
  const cached = cache.get(id);
  if (cached) return cached;
  const promise = ensureDataSource(ref.layer).then((source) => {
    if (!source) {
      cache.delete(id);
      if (useVectorStore.getState().data[ref.layer]?.status === 'error') throw new AreaLoadError(ref.layer);
      throw new AreaMissingError(id);
    }
    const features = (source as VectorSource<Feature<Geometry>>).getFeatures();
    const matcher = matcherFromFeatures(ref, features);
    if (!matcher) throw new AreaMissingError(id);
    const keyField = areaKind(ref.layer).keyField;
    const geometries = features
      .filter((f) => String(f.get(keyField) ?? '').trim() === ref.key)
      .map((f) => f.getGeometry())
      .filter((g): g is Geometry => !!g && (g.getType() === 'Polygon' || g.getType() === 'MultiPolygon'));
    return { matcher, geometries };
  });
  cache.set(id, promise);
  return promise;
}

/** "Tentar de novo": libera as camadas com erro para um novo download. */
export function retryAreaLayers(refs: readonly Pick<AreaRef, 'layer'>[]): void {
  retryFailed([...new Set(refs.map((r) => r.layer))]);
}
